/**
 * F16.L: shared helper for the portal sign-in redirect-mode recovery
 * path. Extracted from `main.tsx` so the redirect detection logic can
 * be unit-tested without booting the React tree.
 *
 * Flow:
 *   1. Before `loginRedirect`, `startPortalRedirectSignIn` (in
 *      `msalInstanceRegistry.ts`) writes a `PortalSignInPending`
 *      record under `PORTAL_SIGNIN_PENDING_KEY` in sessionStorage so
 *      the boot handler can rebuild the PCA + redirect back to the
 *      portal route.
 *   2. When the user returns from `login.microsoftonline.com`, the URL
 *      hash carries `#code=...&state=...` (or `#error=...`).
 *      `isPortalSignInResponse()` detects that case so `main.tsx` can
 *      run the recovery path *before* React boots — TanStack Router
 *      would otherwise strip the hash before MSAL reads it.
 */

export const PORTAL_SIGNIN_PENDING_KEY = 'repancy:portal-signin-pending';

export interface PortalSignInPending {
  slug: string;
  lockedTenantId: string;
  returnTo: string;
  startedAt: string;
}

/**
 * MSAL stashes the OAuth response hash here when it has not yet been
 * consumed by `handleRedirectPromise`. Keyed by clientId, so we have to
 * scan for any key ending in `.urlHash`. Used as a fallback signal when
 * the URL hash itself has already been navigated away.
 */
function hasMsalStashedHash(): boolean {
  try {
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key !== null && key.startsWith('msal.') && key.endsWith('.urlHash')) {
        return true;
      }
    }
  } catch {
    // sessionStorage unavailable — non-fatal.
  }
  return false;
}

/**
 * Read the pending-sign-in record stashed by
 * `startPortalRedirectSignIn`. Returns null when no record exists OR
 * the record is malformed. Idempotent — does NOT clear the record;
 * `clearPortalSignInPending` does that explicitly after the redeem
 * succeeds.
 */
export function readPortalSignInPending(): PortalSignInPending | null {
  try {
    const raw = sessionStorage.getItem(PORTAL_SIGNIN_PENDING_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<PortalSignInPending>;
    if (
      typeof parsed.slug !== 'string' ||
      typeof parsed.lockedTenantId !== 'string' ||
      typeof parsed.returnTo !== 'string' ||
      typeof parsed.startedAt !== 'string'
    ) {
      return null;
    }
    return {
      slug: parsed.slug,
      lockedTenantId: parsed.lockedTenantId,
      returnTo: parsed.returnTo,
      startedAt: parsed.startedAt,
    };
  } catch {
    return null;
  }
}

export function clearPortalSignInPending(): void {
  try {
    sessionStorage.removeItem(PORTAL_SIGNIN_PENDING_KEY);
  } catch {
    // sessionStorage unavailable — non-fatal.
  }
}

export function isPortalSignInResponse(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  // Two routes to "this is a pending sign-in":
  //
  //  1. We are the URL Microsoft just redirected to. The hash carries
  //     `#code=...` or `#error=...` and our recovery handler can parse it
  //     directly via MSAL.
  //  2. The URL hash has already been navigated past (TanStack Router,
  //     HMR, a prior PCA `initialize()` that stashed the hash, etc.), but
  //     MSAL has the OAuth response stashed in sessionStorage under
  //     `msal.<clientId>.urlHash`. `handleRedirectPromise` will consume
  //     it from there.
  //
  // In both cases we require `repancy:portal-signin-pending` to be set so
  // we never run recovery on a tab that didn't initiate a portal sign-in.
  let hasPending: boolean;
  try {
    hasPending = sessionStorage.getItem(PORTAL_SIGNIN_PENDING_KEY) !== null;
  } catch {
    return false;
  }
  if (!hasPending) {
    return false;
  }
  const hash = window.location.hash || '';
  const hashHasCode = hash.includes('code=') || hash.includes('error=');
  return hashHasCode || hasMsalStashedHash();
}

/**
 * Boot-time recovery handler. Called by `main.tsx` BEFORE React boots
 * when `isPortalSignInResponse()` is true.
 *
 *   1. Reads the pending record to identify which tenant initiated the
 *      sign-in (we can't recover this from MSAL alone).
 *   2. Acquires the MSAL instance for that tenant (idempotent +
 *      cached across React re-mounts).
 *   3. Calls `handleRedirectPromise()` so MSAL consumes the URL hash +
 *      sets the active account.
 *   4. Clears the pending record.
 *   5. Returns the `returnTo` URL so `main.tsx` can `history.replaceState`
 *      back to the portal route before React boots — TanStack Router
 *      would otherwise see the `#code=` hash and try to parse it as
 *      a route.
 *
 * Returns null when there's nothing to recover (or on a hard error
 * inside MSAL — failures are logged + propagated to the next silent
 * acquire which will throw InteractionRequiredAuthError).
 */
export async function handlePortalSignInResponse(args: {
  acquireInstance: (lockedTenantId: string) => Promise<{
    handleRedirectPromise: () => Promise<{ account: unknown } | null>;
    setActiveAccount: (account: unknown) => void;
  }>;
}): Promise<{ returnTo: string } | null> {
  const pending = readPortalSignInPending();
  if (pending === null) return null;
  try {
    const instance = await args.acquireInstance(pending.lockedTenantId);
    const result = await instance.handleRedirectPromise();
    if (result !== null && (result as { account?: unknown }).account != null) {
      instance.setActiveAccount((result as { account: unknown }).account);
    }
  } catch (err) {
    console.warn(
      '[portalSignInRedirect] handleRedirectPromise failed:',
      err,
    );
    // Fall through — clear pending + return the returnTo so the user
    // at least lands back on the portal. The next acquireGraphToken
    // call will surface InteractionRequiredAuthError, which the panel
    // already handles via the Reconnect button.
  }
  clearPortalSignInPending();
  return { returnTo: pending.returnTo };
}
